import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  Avatar,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import {
  Send as SendIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  WhatsApp as WhatsAppIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  ArrowBack as ArrowBackIcon,
  SmartToy as SmartToyIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  Stop as StopIcon,
  Search as SearchIcon,
  MedicalServices as MedicalServicesIcon,
  PersonOutline as PersonOutlineIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { io, Socket } from 'socket.io-client';
import { getApiUrl, getWebSocketUrl } from '../../config/environment';
import BotToggleButton from '../../components/BotToggleButton';
import QuickMessagesButton from '../../components/QuickMessagesButton';
import AttendantAssignment from '../../components/AttendantAssignment';
import InboxCrmStageMenu from '../../components/InboxCrmStageMenu';
import HealthcarePanel from '../../components/HealthcarePanel';
import ChatSupportEventsBanner from '../../components/ChatSupportEventsBanner';
import LeadContactTags, { resolveConversationContactId } from '../../components/Inbox/LeadContactTags';
import InboxConversationTagFilter, {
  inboxTagFilterQuery
} from '../../components/Inbox/InboxConversationTagFilter';
import InboxConversationListItem from '../../components/Inbox/InboxConversationListItem';
import InboxPatientSidebar from '../../components/Inbox/InboxPatientSidebar';
import { getInboxAvatarColors, getInboxInitials } from '../../utils/inboxAvatarColors';

/** Estilo do avatar de contato — mesma paleta da lista de conversas (fundo pastel + iniciais coloridas). */
const contactAvatarSx = (name: string | undefined, size: number) => {
  const { bg, color } = getInboxAvatarColors(name || '?');
  return {
    bgcolor: bg,
    color,
    width: size,
    height: size,
    fontSize: size <= 36 ? '0.75rem' : '0.9rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
  };
};
import { inboxService, DynamicInboxConfig } from '../../services/inboxService';

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  source?: string;
  custom_fields?: {
    whatsapp_id?: string;
    username?: string;
  };
}

interface Conversation {
  id: string;
  contact_id: string;
  channel_id: string;
  external_id?: string;
  last_message?: string;
  last_message_at?: string;
  last_message_direction?: 'inbound' | 'outbound';
  last_message_status?: string;
  unread_count: number;
  status: string;
  created_at: string;
  updated_at: string;
  contact?: Contact;
  channel?: {
    id: string;
    name: string;
    type: string;
    status: string;
  };
  contact_tags?: Array<{ id: string; name: string; color: string | null }>;
}

interface Message {
  id: string;
  conversation_id: string;
  content: string;
  direction: 'inbound' | 'outbound';
  sent_at: string;
  status: string;
  content_type?: string;
  attachments?: Array<{
    type: string;
    url: string;
    file_id?: string;
    width?: number;
    height?: number;
    file_name?: string;
    mime_type?: string;
    duration?: number;
    title?: string;
    performer?: string;
    emoji?: string;
  }>;
}

interface Channel {
  id: string;
  name: string;
  type: string;
  status: string;
}

/** Número ao lado do nome: phone do contato, JID em custom_fields ou external_id da conversa. */
function formatConversationPhoneDisplay(conv: Conversation | null | undefined): string | null {
  if (!conv) return null;
  const c = conv.contact;
  const explicit = String(c?.phone ?? '').trim();
  if (explicit) {
    if (explicit.startsWith('+')) return explicit;
    const digits = explicit.replace(/\D/g, '');
    if (digits.length >= 10) return `+${digits}`;
    return explicit;
  }

  let fromWa = String(c?.custom_fields?.whatsapp_id ?? '').trim();
  if (fromWa) {
    const at = fromWa.indexOf('@');
    if (at > 0) fromWa = fromWa.slice(0, at);
    fromWa = fromWa.replace(/^whatsapp:/i, '');
    const digits = fromWa.replace(/\D/g, '');
    if (digits.length >= 8) return `+${digits}`;
  }

  let ext = String(conv.external_id ?? '').trim();
  ext = ext.replace(/^whatsapp:/i, '');
  const at2 = ext.indexOf('@');
  if (at2 > 0) ext = ext.slice(0, at2);
  const d2 = ext.replace(/\D/g, '');
  if (d2.length >= 8) return `+${d2}`;
  return null;
}

const DynamicWhatsAppInbox: React.FC = () => {
  const { inboxSlug } = useParams<{ inboxSlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const outletContext = useOutletContext<{ toggleDrawer?: () => void }>();
  
  // Estado da configuração do inbox
  const [inboxConfig, setInboxConfig] = useState<DynamicInboxConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string>('');
  
  // Constantes para URLs da API e WebSocket
  const API_BASE_URL = getApiUrl();
  const WS_URL = getWebSocketUrl();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string>('');
  const selectedConversationRef = useRef<string>('');
  
  // Atualizar ref quando selectedConversation mudar
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [updatingAvatar, setUpdatingAvatar] = useState<string | null>(null);
  const [deletingContact, setDeletingContact] = useState<string | null>(null);
  const [openLeadDetailsDialog, setOpenLeadDetailsDialog] = useState(false);
  const [openHealthcarePanel, setOpenHealthcarePanel] = useState(false);
  const [healthcarePanelTab, setHealthcarePanelTab] = useState(0);
  const [healthcareOpenSchedule, setHealthcareOpenSchedule] = useState(false);
  const [patientSidebarReload, setPatientSidebarReload] = useState(0);
  const [mobilePatientDrawer, setMobilePatientDrawer] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  // Attach image to patient gallery
  const [attachingToGallery, setAttachingToGallery] = useState(false);
  const [attachGalleryMsg, setAttachGalleryMsg] = useState<string>('');
  const [stage4Leads, setStage4Leads] = useState<Set<string>>(new Set());
  const [allLeadsStages, setAllLeadsStages] = useState<Map<string, number>>(new Map());
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [inboxFilterTagIds, setInboxFilterTagIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLDivElement>(null);
  const recordingIntervalRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  /** Ao cancelar durante gravação, não criar ficheiro no onstop */
  const discardRecordingRef = useRef(false);
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Carregar configuração do inbox
  useEffect(() => {
    const loadInboxConfig = async () => {
      if (!inboxSlug) {
        setConfigError('Inbox não especificado');
        setConfigLoading(false);
        return;
      }

      try {
        setConfigLoading(true);
        // O inboxSlug já vem completo da URL (ex: "whatsapp-recovery-vip")
        const path = `/inbox/${inboxSlug}`;
        const config = await inboxService.getInboxByPath(path);
        
        if (!config) {
          setConfigError(`Inbox "${inboxSlug}" não encontrado`);
          setConfigLoading(false);
          return;
        }

        console.log('✅ Inbox config carregada:', config);
        console.log('📋 channelId disponível?', config.channelId ? 'SIM' : 'NÃO');
        
        if (!config.channelId) {
          console.warn('⚠️ channelId não encontrado. Tentando sincronizar canais...');
          // Tentar sincronizar canais primeiro
          try {
            const syncResponse = await inboxService.syncChannels();
            console.log('🔄 Resposta da sincronização:', syncResponse);
            
            // Pequeno delay para garantir que o backend processou a sincronização
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Invalidar cache e buscar novamente após sincronização
            inboxService.invalidateCache();
            const updatedConfig = await inboxService.getInboxByPath(path);
            
            if (updatedConfig) {
              console.log('✅ Config atualizada após sincronização:', updatedConfig);
              console.log('📋 channelId após sincronização:', updatedConfig.channelId ? 'SIM' : 'NÃO');
              setInboxConfig(updatedConfig);
            } else {
              console.warn('⚠️ Config não encontrada após sincronização, usando config original');
              setInboxConfig(config);
            }
          } catch (syncError) {
            console.error('❌ Erro ao sincronizar:', syncError);
            setInboxConfig(config); // Usar config mesmo sem channelId
          }
        } else {
          setInboxConfig(config);
        }
        
        setConfigError('');
      } catch (err: any) {
        console.error('❌ Erro ao carregar configuração do inbox:', err);
        setConfigError(err.message || 'Erro ao carregar inbox');
      } finally {
        setConfigLoading(false);
      }
    };

    loadInboxConfig();
  }, [inboxSlug]);

  // Abrir conversa específica se contactId estiver na URL
  // 🔥 CORREÇÃO: Aguardar inboxConfig estar carregado e usar channelId específico
  useEffect(() => {
    const contactId = searchParams.get('contactId');
    
    // Aguardar inboxConfig estar carregado
    if (!inboxConfig?.channelId || !contactId) {
      return;
    }
    
    // Se há um contactId na URL, buscar conversas diretamente desse contato
    const fetchConversationForContact = async () => {
      try {
        console.log(`🔍 Buscando conversa para contactId: ${contactId} no inbox: ${inboxConfig.name} (channelId: ${inboxConfig.channelId})`);
        
        // 🔥 CORREÇÃO: Buscar conversas específicas desse contato NESTE inbox (usando channelId)
        const contactConversationResponse = await axios.get(
          `${API_BASE_URL}/conversations?channel_id=${inboxConfig.channelId}&contact_id=${contactId}&limit=10`
        );
        
        if (contactConversationResponse.data.success) {
          const contactConversations = contactConversationResponse.data.data || [];
          
          if (contactConversations.length > 0) {
            // Pegar a conversa mais recente desse contato
            const mostRecentConversation = contactConversations.sort((a: any, b: any) => {
              const dateA = new Date(a.last_message_at || a.updated_at || a.created_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || b.created_at || 0);
              return dateB.getTime() - dateA.getTime();
            })[0];
            
            console.log('✅ Conversa encontrada:', mostRecentConversation.id);
            setSelectedConversation(mostRecentConversation.id);
            
            // IMPORTANTE: Carregar TODAS as mensagens desta conversa quando há contactId na URL
            // Isso garante que mensagens antigas sejam carregadas mesmo que não estejam entre as últimas 50
            await fetchMessages(mostRecentConversation.id, true); // true = loadAll
            
            // 🔥 CORREÇÃO: Buscar também as outras conversas DESTE inbox (não genérico)
            const allConversationsResponse = await axios.get(
              `${API_BASE_URL}/conversations?channel_id=${inboxConfig.channelId}&limit=50&page=1&sortBy=last_message_at&sortOrder=desc`
            );
            
            if (allConversationsResponse.data.success) {
              const allConversations = allConversationsResponse.data.data || [];
              
              // Garantir que a conversa selecionada esteja na lista
              const conversationExists = allConversations.some((c: any) => c.id === mostRecentConversation.id);
              if (!conversationExists) {
                allConversations.unshift(mostRecentConversation);
              }
              
              setConversations(allConversations);
            }
          } else {
            console.warn(`⚠️ Nenhuma conversa encontrada para este contato no inbox ${inboxConfig.name}`);
            // Buscar todas as conversas deste inbox normalmente
            const response = await axios.get(
              `${API_BASE_URL}/conversations?channel_id=${inboxConfig.channelId}&limit=50&page=1&sortBy=last_message_at&sortOrder=desc`
            );
            if (response.data.success) {
              setConversations(response.data.data || []);
            }
          }
          
          // Limpar o parâmetro da URL após abrir a conversa
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('contactId');
          window.history.replaceState({}, '', newUrl.toString());
        }
      } catch (error) {
        console.error('❌ Erro ao buscar conversas do inbox:', error);
        // Em caso de erro, buscar todas as conversas deste inbox normalmente
        try {
          const response = await axios.get(
            `${API_BASE_URL}/conversations?channel_id=${inboxConfig.channelId}&limit=50&page=1&sortBy=last_message_at&sortOrder=desc`
          );
          if (response.data.success) {
            setConversations(response.data.data || []);
          }
        } catch (err) {
          console.error('❌ Erro ao buscar conversas após falha:', err);
        }
      }
    };
    
    fetchConversationForContact();
  }, [searchParams, inboxConfig?.channelId]); // 🔥 Adicionada dependência do channelId

  // Função para atualizar avatar do contato
  const updateContactAvatar = async (contactId: string) => {
    try {
      setUpdatingAvatar(contactId);
      const response = await axios.post(`${API_BASE_URL}/contacts/${contactId}/update-avatar`);
      
      if (response.data.success) {
        // Atualizar a lista de conversas para refletir a mudança
        await fetchConversations();
        
        // Se a conversa estiver selecionada, atualizar as mensagens também
        if (selectedConversation) {
          await fetchMessages(selectedConversation);
        }
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      setError('Erro ao atualizar foto de perfil');
    } finally {
      setUpdatingAvatar(null);
    }
  };

  // Função para excluir contato
  const deleteContact = async (contactId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este contato? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setDeletingContact(contactId);
      const response = await axios.delete(`${API_BASE_URL}/contacts/${contactId}`);
      
      if (response.data.success) {
        // Remover a conversa da lista
        setConversations(prev => prev.filter(conv => conv.contact_id !== contactId));
        
        // Se a conversa excluída estava selecionada, limpar a seleção
        if (selectedConversation && conversations.find(c => c.id === selectedConversation)?.contact_id === contactId) {
          setSelectedConversation('');
          setMessages([]);
        }
        
        setError('');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      setError('Erro ao excluir contato');
    } finally {
      setDeletingContact(null);
    }
  };

  // Função para scroll automático para a última mensagem
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Função para normalizar URLs de anexos
  const normalizeAttachmentUrl = (url: string | undefined): string => {
    if (!url) return '';
    // Se já é uma URL completa (http/https), retornar como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Se for URL relativa, construir URL completa usando a base da API
    const apiBase = API_BASE_URL.replace('/api/v1', '');
    return url.startsWith('/') ? `${apiBase}${url}` : `${apiBase}/${url}`;
  };

  // Função para buscar conversas (memoizada)
  const fetchConversations = useCallback(async (reset: boolean = true) => {
    if (!inboxConfig?.channelId) {
      console.warn('⚠️ channelId não disponível, aguardando configuração do inbox');
      return;
    }

    try {
      setError('');
      
      if (reset) {
        setConversationsPage(1);
        setHasMoreConversations(true);
      }
      
      const page = reset ? 1 : conversationsPage;
      const limit = 100; // Reduzir de 200 para 100 para carregar mais rápido
      
      console.log(`🔍 Buscando conversas para inbox: ${inboxConfig.name} (channel_id: ${inboxConfig.channelId})`);
      
      // Buscar conversas do banco de dados com paginação (apenas uma vez)
      const tagParam = inboxTagFilterQuery(inboxFilterTagIds);
      const conversationsResponse = await axios.get(`${API_BASE_URL}/conversations?channel_id=${inboxConfig.channelId}&limit=${limit}&page=${page}&sortBy=last_message_at&sortOrder=desc${tagParam}`, {
        timeout: 30000
      });
      
      if (conversationsResponse.data.success) {
        const newConversations: Conversation[] = conversationsResponse.data.data || [];
        const pagination = conversationsResponse.data.pagination;
        
        // Filtrar conversas vazias (sem last_message_at válido)
        const conversationsWithMessages = newConversations.filter((conv: any) => {
          return conv.last_message_at && conv.last_message_at !== null;
        });
        
        console.log(`🔍 ${inboxConfig.name} - Conversas antes do filtro: ${newConversations.length}, depois: ${conversationsWithMessages.length}`);
        
        // Verificar se há mais conversas para carregar
        const hasMore = pagination ? page < pagination.totalPages : conversationsWithMessages.length === limit;
        setHasMoreConversations(hasMore);
        
        // Ordenar conversas por last_message_at (mais recentes primeiro)
        const sortedNewConversations = conversationsWithMessages.sort((a: any, b: any) => {
          const dateA = new Date(a.last_message_at || a.updated_at || 0);
          const dateB = new Date(b.last_message_at || b.updated_at || 0);
          return dateB.getTime() - dateA.getTime();
        });
        
        if (reset) {
          setConversations(sortedNewConversations);
          // Verificar estágios de todos os leads
          await checkAllLeadsStages(sortedNewConversations);
        } else {
          // Adicionar novas conversas às existentes, evitando duplicatas
          setConversations(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const uniqueNew = sortedNewConversations.filter(c => !existingIds.has(c.id));
            const combined = [...prev, ...uniqueNew].sort((a: any, b: any) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
            return combined;
          });
        }
        
        // Atualizar página
        setConversationsPage(page + 1);
      }
    } catch (err) {
      console.error('Erro ao buscar conversas:', err);
      // Não mostrar erro para o usuário se for apenas timeout
      if (err && typeof err === 'object' && 'code' in err && err.code !== 'ECONNABORTED') {
        setError('Erro ao carregar conversas');
      }
    } finally {
      setLoading(false);
      setLoadingMoreConversations(false);
    }
  }, [conversationsPage, inboxConfig?.channelId, inboxConfig?.name, inboxFilterTagIds]);

  // Função para carregar mais conversas
  const loadMoreConversations = useCallback(async () => {
    if (loadingMoreConversations || !hasMoreConversations) return;
    
    setLoadingMoreConversations(true);
    await fetchConversations(false);
  }, [loadingMoreConversations, hasMoreConversations, fetchConversations]);

  // Função para marcar conversa como lida
  const markConversationAsRead = useCallback(async (conversationId: string) => {
    try {
      // Verificar se é uma conversa de instância (não existe no banco)
      if (conversationId.startsWith('instance-')) {
        console.log('📱 Marcando conversa de instância como lida:', conversationId);
        // Para conversas de instância, apenas atualizar o estado local
        setConversations(prev => {
          const updated = prev.map(conv => 
            conv.id === conversationId 
              ? { ...conv, unread_count: 0 }
              : conv
          );
          console.log('📱 Estado atualizado:', updated.find(c => c.id === conversationId));
          return updated;
        });
        return;
      }

      await axios.post(`${API_BASE_URL}/conversations/${conversationId}/mark-as-read`);
      
      // Atualizar a lista de conversas para refletir a mudança
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, unread_count: 0 }
          : conv
      ));
      window.dispatchEvent(new CustomEvent('inbox:menu-unread-refresh'));
    } catch (err) {
      console.error('Erro ao marcar conversa como lida:', err);
      // Não mostrar erro para o usuário, pois não é crítico
    }
  }, []);

  // Log das mensagens quando mudarem
  useEffect(() => {
    console.log(`🎨 Mensagens no estado: ${messages.length}`);
    messages.forEach((message, index) => {
      console.log(`📝 Mensagem ${index + 1}/${messages.length}: ${message.content.substring(0, 30)}...`);
    });
  }, [messages]);

  // Função para buscar mensagens de uma conversa
  const fetchMessages = useCallback(async (conversationId: string, loadAll: boolean = false) => {
    try {
      console.log(`🔍 Buscando mensagens para conversa: ${conversationId}, loadAll: ${loadAll}`);
      
      // Verificar se é uma conversa de instância (não existe no banco)
      if (conversationId.startsWith('instance-')) {
        console.log('📱 Conversa de instância detectada, buscando mensagens das instâncias...');
        
        // Extrair contactId e instanceName do ID da conversa
        // O formato é: instance-{contactId}-{instanceName}
        const instanceKey = conversationId.replace('instance-', '');
        
        // O contactId é um UUID completo (36 caracteres: 8-4-4-4-12)
        // Então precisamos pegar os primeiros 36 caracteres como contactId
        const contactId = instanceKey.substring(0, 36);
        const instanceName = instanceKey.substring(37); // Pular o hífen após o UUID
        
        console.log('🔍 Extraído:', { contactId, instanceName, originalId: conversationId });
        
        // Buscar mensagens das instâncias WhatsApp
        const response = await axios.get(`${API_BASE_URL}/whatsapp-instance-messages?contact_id=${contactId}&instance_name=${instanceName}&limit=1000`, {
          timeout: 30000
        });
        
        if (response.data.success) {
          let messagesData: Message[] = response.data.data || [];
          console.log(`📋 Total de mensagens de instância recebidas: ${messagesData.length}`);
          
          // Normalizar attachments para garantir que seja sempre um array
          messagesData = messagesData.map(msg => {
            if (msg.attachments) {
              if (Array.isArray(msg.attachments)) {
                return { ...msg, attachments: msg.attachments };
              } else if (typeof msg.attachments === 'string') {
                try {
                  return { ...msg, attachments: JSON.parse(msg.attachments) };
                } catch (e) {
                  console.error(`Erro ao parsear attachments da mensagem ${msg.id}:`, e);
                  return { ...msg, attachments: [] };
                }
              } else {
                return { ...msg, attachments: [] };
              }
            } else {
              return { ...msg, attachments: [] };
            }
          });
          
          // Ordenar mensagens por data (mais antigas primeiro, mais novas no final)
          const sortedMessages = messagesData.sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime());
          console.log(`📋 Mensagens de instância ordenadas: ${sortedMessages.length}`);
          
          setMessages(sortedMessages);
          console.log(`🎯 Mensagens de instância definidas no estado: ${sortedMessages.length}`);
          
          // Scroll para a última mensagem após um pequeno delay
          setTimeout(() => {
            console.log('🔄 Executando scroll para baixo...');
            scrollToBottom();
          }, 100);
        } else {
          console.warn('Nenhuma mensagem encontrada para a conversa de instância');
          setMessages([]);
        }
        return;
      }
      
      // Para conversas normais, usar o endpoint padrão
      // Se loadAll=true, usar limit muito alto para sinalizar ao backend que deve carregar tudo
      const limit = loadAll ? 10000 : 1000;
      const response = await axios.get(`${API_BASE_URL}/messages/conversation/${conversationId}?limit=${limit}&sortOrder=asc`, {
        timeout: 30000 // 30 segundos de timeout
      });
      
      console.log('📊 Resposta da API:', response.data);
      let messagesData: Message[] = response.data.data || [];
      console.log(`📋 Total de mensagens recebidas: ${messagesData.length}`);
      
      // Normalizar attachments para garantir que seja sempre um array
      messagesData = messagesData.map(msg => {
        if (msg.attachments) {
          if (Array.isArray(msg.attachments)) {
            return { ...msg, attachments: msg.attachments };
          } else if (typeof msg.attachments === 'string') {
            try {
              return { ...msg, attachments: JSON.parse(msg.attachments) };
            } catch (e) {
              console.error(`Erro ao parsear attachments da mensagem ${msg.id}:`, e);
              return { ...msg, attachments: [] };
            }
          } else {
            return { ...msg, attachments: [] };
          }
        } else {
          return { ...msg, attachments: [] };
        }
      });
      
      // Ordenar mensagens por data (mais antigas primeiro, mais novas no final)
      const sortedMessages = messagesData.sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime());
      console.log(`📋 Mensagens ordenadas: ${sortedMessages.length}`);
      
      setMessages(sortedMessages);
      console.log(`🎯 Mensagens definidas no estado: ${sortedMessages.length}`);
      
      // Scroll para a última mensagem após um pequeno delay
      setTimeout(() => {
        console.log('🔄 Executando scroll para baixo...');
        scrollToBottom();
      }, 100);
    } catch (err) {
      console.error('Erro ao buscar mensagens:', err);
      // Não mostrar erro para o usuário se for apenas timeout
      if (err && typeof err === 'object' && 'code' in err && err.code !== 'ECONNABORTED') {
        setError('Erro ao carregar mensagens');
      }
    }
  }, []);

  // Função para enviar mensagem
  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation || sendingMessage) return;

    // Se houver arquivo selecionado, enviar como mídia
    if (selectedFile) {
      await handleSendMedia(selectedFile);
      return;
    }

    // Caso contrário, enviar mensagem de texto normal
    setSendingMessage(true);
    try {
      await axios.post(`${API_BASE_URL}/messages/send`, {
        conversation_id: selectedConversation,
        content: newMessage,
        content_type: 'text'
      });

      setNewMessage('');
      // A mensagem será adicionada via WebSocket
      // Scroll para o final após enviar
      setTimeout(scrollToBottom, 200);
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err);
      setError('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para enviar mídia
  const handleSendMedia = async (file: File) => {
    if (!selectedConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversation_id', selectedConversation);
      if (newMessage.trim()) {
        formData.append('content', newMessage);
      }
      // Se for um arquivo de áudio gravado
      if (file.name.includes('recording') || file.name.includes('voice')) {
        formData.append('is_voice', 'true');
      }

      const response = await axios.post(`${API_BASE_URL}/messages/send-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Sempre recarregar mensagens após envio bem-sucedido para garantir que apareçam
      setNewMessage('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Aguardar um pouco para garantir que o banco foi atualizado e WebSocket processou
      setTimeout(() => {
        fetchMessages(selectedConversation);
        setTimeout(scrollToBottom, 300);
      }, 800);
    } catch (err: any) {
      console.error('Erro ao enviar mídia:', err);
      setError(err.response?.data?.error || 'Erro ao enviar mídia');
    } finally {
      setSendingMessage(false);
    }
  };

  // Função para lidar com paste de imagens (Ctrl+V)
  const handlePasteImage = useCallback((e: React.ClipboardEvent) => {
    if (!selectedConversation) return;
    
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Verificar se é uma imagem
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = item.getAsFile();
        
        if (file) {
          // Criar um nome descritivo para o arquivo
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const renamedFile = new File(
            [file],
            `pasted-image-${timestamp}.png`,
            { type: file.type }
          );
          
          // Definir como arquivo selecionado (mostrará preview)
          setSelectedFile(renamedFile);
        }
        break;
      }
    }
  }, [selectedConversation]);

  // Função para selecionar arquivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tamanho (50MB)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('Arquivo muito grande. Tamanho máximo: 50MB');
        return;
      }

      // Validar tipo (inclui documentos: PDF, Word, Excel, PPT, texto, ZIP)
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        'video/mp4', 'video/mpeg', 'video/quicktime',
        'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/aac', 'audio/m4a',
        'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain', 'application/zip'
      ];
      const ext = (file.name.toLowerCase().split('.').pop() || '');
      const isPdf = ext === 'pdf' && (file.type === 'application/pdf' || !file.type);
      const isDoc = (ext === 'doc' || ext === 'docx') && (['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ''].includes(file.type));
      const isExcel = (ext === 'xls' || ext === 'xlsx') && (['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ''].includes(file.type));
      const isPpt = (ext === 'ppt' || ext === 'pptx') && (['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation', ''].includes(file.type));
      const isTxt = ext === 'txt' && (file.type === 'text/plain' || !file.type);
      const isZip = ext === 'zip' && (file.type === 'application/zip' || !file.type);
      const hasValidType = allowedTypes.includes(file.type) || isPdf || isDoc || isExcel || isPpt || isTxt || isZip;
      if (!hasValidType) {
        setError('Tipo de arquivo não suportado');
        return;
      }

      setSelectedFile(file);
      setError('');
    }
  };

  // Contador da gravação: tem de viver num useEffect [isRecording].
  // Se o setInterval viver ao lado de estado que actualiza logo (ex.: cleanup que dependia do recorder),
  // o intervalo pode ser apagado na mesma digestão e o UI fica sempre 0:00.
  useEffect(() => {
    if (!isRecording) return;
    recordingIntervalRef.current = window.setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
    return () => {
      if (recordingIntervalRef.current != null) {
        window.clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    };
  }, [isRecording]);

  // Função para iniciar gravação de áudio
  const handleStartRecording = async () => {
    try {
      discardRecordingRef.current = false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;

      const mimeCandidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
      ];
      const mimeType = mimeCandidates.find((t) => MediaRecorder.isTypeSupported(t));

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        try {
          recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
        } catch {
          /* noop */
        }
        recordingStreamRef.current = null;
        mediaRecorderRef.current = null;

        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          return;
        }

        const outType = recorder.mimeType?.split(';')[0] || 'audio/webm';
        const audioBlob = new Blob(chunks, { type: outType });
        const ext = outType.includes('mp4') ? 'm4a' : 'webm';
        const audioFile = new File([audioBlob], `recording-${Date.now()}.${ext}`, { type: outType });
        setSelectedFile(audioFile);
      };

      mediaRecorderRef.current = recorder;
      // Timeslice: chunks periódicos melhoram matadados de duração no WebM para o WhatsApp
      recorder.start(250);
      setRecordingTime(0);
      setIsRecording(true);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      setError('Erro ao acessar microfone. Verifique as permissões.');
    }
  };

  // Função para parar gravação de áudio
  const handleStopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') {
      setIsRecording(false);
      return;
    }
    try {
      rec.stop();
    } catch {
      /* noop */
    }
    setIsRecording(false);
  };

  // Função para cancelar arquivo selecionado
  const handleCancelFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (isRecording) {
      discardRecordingRef.current = true;
      handleStopRecording();
    }
  };

  // Desmontagem: largar microfone e timers
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current != null) {
        window.clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      discardRecordingRef.current = true;
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaRecorderRef.current = null;
      recordingStreamRef.current = null;
    };
  }, []);

  /**
   * Pedidos em voo de `/conversations/:id`, para deduplicar buscas quando várias
   * mensagens chegam em rajada para a mesma conversa que ainda não está na lista.
   */
  const inflightConversationFetchRef = useRef<Map<string, Promise<unknown>>>(new Map());
  /** Snapshot atual de conversations para uso dentro de listeners de socket. */
  const conversationsRef = useRef<Conversation[]>([]);
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  /** Aplica updates de uma mensagem nova à lista/mensagens (já com canal validado). */
  const applyNewMessageToState = useCallback(
    (message: any) => {
      if (message.conversation_id === selectedConversationRef.current) {
        setMessages((prev) => {
          if (prev.some((msg) => msg.id === message.id)) return prev;
          const next = [...prev, message];
          return next.sort(
            (a, b) =>
              new Date(a.sent_at || a.created_at).getTime() -
              new Date(b.sent_at || b.created_at).getTime(),
          );
        });
        setTimeout(scrollToBottom, 100);
      }

      setConversations((prev) => {
        const idx = prev.findIndex((conv) => conv.id === message.conversation_id);
        if (idx < 0) return prev;
        const conv = prev[idx];
        const isCurrentlySelected = conv.id === selectedConversationRef.current;
        const updatedConv: Conversation = {
          ...conv,
          last_message_at:
            message.sent_at || message.created_at || new Date().toISOString(),
          last_message: message.content
            ? message.content.substring(0, 100)
            : conv.last_message,
          last_message_direction: message.direction,
          last_message_status: message.status,
          unread_count:
            message.direction === 'inbound' && !isCurrentlySelected
              ? (conv.unread_count || 0) + 1
              : isCurrentlySelected
              ? 0
              : conv.unread_count || 0,
        };
        const next = [...prev];
        next[idx] = updatedConv;
        return next.sort((a, b) => {
          const dateA = new Date(a.last_message_at || a.updated_at || 0);
          const dateB = new Date(b.last_message_at || b.updated_at || 0);
          return dateB.getTime() - dateA.getTime();
        });
      });
    },
    [],
  );

  /**
   * Conexão WebSocket: depende de `inboxConfig.channelId` para criar/derrubar o
   * socket. O `join/leave_conversation` é feito em um efeito separado mais
   * abaixo, controlado por `selectedConversation`.
   */
  useEffect(() => {
    const channelId = inboxConfig?.channelId;
    if (!channelId) {
      console.warn('⚠️ channelId não disponível, aguardando configuração para WebSocket');
      return;
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      auth: { token },
      query: { channelId },
    });
    socketRef.current = socket;

    const subscribeChannel = () => {
      socket.emit('subscribe_channel', channelId);
      if (selectedConversationRef.current) {
        socket.emit('join_conversation', selectedConversationRef.current);
      }
    };

    socket.on('connect', () => {
      console.log(
        `✅ Connected to WebSocket for ${inboxConfig?.name} (channel_id: ${channelId})`,
      );
      subscribeChannel();
    });
    socket.on('reconnect', subscribeChannel);

    socket.on('new_message', (data: any) => {
      let message = data?.message || data;
      if (!message) return;

      if (!message.conversation_id) {
        message.conversation_id = data?.conversation_id || message.conversation_id;
      }
      if (!message.conversation_id) return;

      if (message.attachments) {
        if (Array.isArray(message.attachments)) {
          message = { ...message, attachments: message.attachments };
        } else if (typeof message.attachments === 'string') {
          try {
            message = { ...message, attachments: JSON.parse(message.attachments) };
          } catch {
            message = { ...message, attachments: [] };
          }
        } else {
          message = { ...message, attachments: [] };
        }
      } else {
        message = { ...message, attachments: [] };
      }

      // Se o payload trouxer channel_id, filtra antes de tocar no estado.
      const payloadChannelId =
        message.channel_id || message.channel?.id || data?.channel_id || null;
      if (payloadChannelId && String(payloadChannelId) !== String(channelId)) {
        return;
      }

      const conversationKnown = conversationsRef.current.some(
        (c) => c.id === message.conversation_id,
      );
      if (conversationKnown) {
        applyNewMessageToState(message);
        return;
      }

      // Conversa ainda não está na lista — buscar via API filtrando por canal.
      const convId: string = message.conversation_id;
      if (inflightConversationFetchRef.current.has(convId)) return;
      const fetchPromise = axios
        .get(
          `${API_BASE_URL}/conversations/${convId}?channel_id=${encodeURIComponent(channelId)}`,
        )
        .then((response) => {
          if (!response?.data?.success || !response.data.data) return;
          const newConversation: Conversation = response.data.data;
          const convChannelId =
            newConversation.channel_id || newConversation.channel?.id;
          if (convChannelId && String(convChannelId) !== String(channelId)) return;

          setConversations((prev) => {
            if (prev.some((c) => c.id === newConversation.id)) return prev;
            const updated = [newConversation, ...prev];
            return updated.sort((a, b) => {
              const dateA = new Date(a.last_message_at || a.updated_at || 0);
              const dateB = new Date(b.last_message_at || b.updated_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
          });
          applyNewMessageToState(message);
        })
        .catch(() => {
          /* 404 / canal incompatível — ignora silenciosamente */
        })
        .finally(() => {
          inflightConversationFetchRef.current.delete(convId);
        });

      inflightConversationFetchRef.current.set(convId, fetchPromise);
    });

    socket.on('conversation_updated', (data: any) => {
      if (!data?.conversationId || !data?.update) return;
      const payloadChannelId =
        data.update?.channel_id || data.channel_id || null;
      if (payloadChannelId && String(payloadChannelId) !== String(channelId)) {
        return;
      }

      setConversations((prev) => {
        if (!prev.some((c) => c.id === data.conversationId)) return prev;
        const updated = prev.map((conv) =>
          conv.id === data.conversationId
            ? {
                ...conv,
                last_message_at: data.update.last_message_at || conv.last_message_at,
                unread_count:
                  data.update.unread_count !== undefined
                    ? data.update.unread_count
                    : conv.unread_count,
                status: data.update.status || conv.status,
              }
            : conv,
        );
        return updated.sort((a, b) => {
          const dateA = new Date(a.last_message_at || a.updated_at || 0);
          const dateB = new Date(b.last_message_at || b.updated_at || 0);
          return dateB.getTime() - dateA.getTime();
        });
      });
    });

    socket.on('disconnect', () => {
      console.log('WebSocket desconectado');
    });

    socket.on('connect_error', (error: any) => {
      console.error('Erro de conexão WebSocket:', error);
    });

    return () => {
      try {
        socket.emit('unsubscribe_channel', channelId);
      } catch {
        /* noop */
      }
      socket.removeAllListeners();
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      inflightConversationFetchRef.current.clear();
    };
  }, [inboxConfig?.channelId, inboxConfig?.name, applyNewMessageToState]);

  /** Entra/sai da sala da conversa selecionada (independente da criação do socket). */
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !selectedConversation) return;

    socket.emit('join_conversation', selectedConversation);
    return () => {
      try {
        socket.emit('leave_conversation', selectedConversation);
      } catch {
        /* noop */
      }
    };
  }, [selectedConversation]);

  // Carregar conversas iniciais (apenas quando inboxConfig estiver pronto com channelId)
  useEffect(() => {
    if (!inboxConfig) return;

    if (inboxConfig.channelId) {
      console.log('✅ Inbox config pronta, carregando conversas...', {
        channelId: inboxConfig.channelId,
        name: inboxConfig.name
      });
      fetchConversations(true);
    } else {
      console.warn('⚠️ Inbox config carregada mas sem channelId. Aguardando sincronização...');
    }
  }, [inboxConfig?.channelId, inboxFilterTagIds]);

  // Carregar mensagens quando uma conversa é selecionada
  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
      // Marcar conversa como lida quando selecionada
      markConversationAsRead(selectedConversation);
    }
  }, [selectedConversation, fetchMessages, markConversationAsRead]);

  // Scroll automático quando mensagens mudarem
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Ontem';
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };

  const openHealthcareForContact = (
    contact: Contact,
    opts?: { tab?: number; schedule?: boolean }
  ) => {
    setSelectedContact(contact);
    setHealthcarePanelTab(opts?.tab ?? 0);
    setHealthcareOpenSchedule(!!opts?.schedule);
    setOpenHealthcarePanel(true);
  };

  const handleContactClick = (contact: Contact) => {
    openHealthcareForContact(contact);
  };

  const handleCloseHealthcarePanel = () => {
    setOpenHealthcarePanel(false);
    setSelectedContact(null);
    setHealthcarePanelTab(0);
    setHealthcareOpenSchedule(false);
    setPatientSidebarReload((t) => t + 1);
  };

  const selectedConversationContact = conversations.find(
    (c) => c.id === selectedConversation
  )?.contact;

  const sendMessageToChat = useCallback(
    async (content: string) => {
      if (!selectedConversation) {
        throw new Error('Selecione uma conversa para enviar a mensagem.');
      }
      await axios.post(`${API_BASE_URL}/messages/send`, {
        conversation_id: selectedConversation,
        content,
        content_type: 'text',
      });
      setTimeout(scrollToBottom, 200);
    },
    [selectedConversation]
  );

  useEffect(() => {
    setMobilePatientDrawer(false);
  }, [selectedConversation]);

  const handleAttachImageToGallery = async (imageUrl: string) => {
    const currentContact = conversations.find((c: any) => c.id === selectedConversation)?.contact;
    if (!currentContact) {
      setAttachGalleryMsg('Nenhum contato encontrado para esta conversa');
      return;
    }
    setAttachingToGallery(true);
    try {
      const { data: panelRes } = await axios.get(`${API_BASE_URL}/clinic/healthcare-panel/contact/${currentContact.id}/summary`);
      if (!panelRes?.success || !panelRes?.data?.linked_patient) {
        setAttachGalleryMsg('Nenhum paciente vinculado a este contato. Vincule um paciente no Painel de Saúde primeiro.');
        return;
      }
      const patientId = panelRes.data.linked_patient.id;
      const { data: res } = await axios.post(`${API_BASE_URL}/clinic/patient-images/from-chat`, {
        patient_id: patientId,
        image_url: imageUrl,
        description: `Imagem do chat`,
      });
      if (res.success) {
        setAttachGalleryMsg('Imagem salva no banco de imagens do paciente!');
      } else {
        setAttachGalleryMsg('Erro ao salvar imagem');
      }
    } catch (err: any) {
      setAttachGalleryMsg(err?.response?.data?.error || 'Erro ao salvar imagem na galeria');
    } finally {
      setAttachingToGallery(false);
    }
  };

  const handleSendMediaFromHealthcare = async (url: string, type: string, fileName?: string) => {
    if (!selectedConversation) return;
    try {
      setSendingMessage(true);
      await axios.post(`${API_BASE_URL}/messages/send`, {
        conversation_id: selectedConversation,
        content: fileName || 'Imagem clínica',
        media_type: type,
        media_url: url,
      });
      setTimeout(() => {
        fetchMessages(selectedConversation);
        setTimeout(scrollToBottom, 300);
      }, 800);
    } catch (err: any) {
      console.error('Erro ao enviar mídia do healthcare:', err);
      setError(err.response?.data?.error || 'Erro ao enviar mídia');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAttachDocToHealthcare = async (docUrl: string, fileName: string, mimeType?: string) => {
    const currentContact = conversations.find((c: any) => c.id === selectedConversation)?.contact;
    if (!currentContact) {
      setAttachGalleryMsg('Nenhum contato encontrado para esta conversa');
      return;
    }
    setAttachingToGallery(true);
    try {
      const { data: panelRes } = await axios.get(`${API_BASE_URL}/clinic/healthcare-panel/contact/${currentContact.id}/summary`);
      if (!panelRes?.success || !panelRes?.data?.linked_patient) {
        setAttachGalleryMsg('Nenhum paciente vinculado a este contato. Vincule um paciente no Painel de Saúde primeiro.');
        return;
      }
      const patientId = panelRes.data.linked_patient.id;
      const { data: res } = await axios.post(`${API_BASE_URL}/clinic/patients/${patientId}/documents/from-chat`, {
        patient_id: patientId,
        document_url: docUrl,
        file_name: fileName,
        file_type: mimeType || 'application/octet-stream',
        description: `Documento do chat`,
      });
      if (res.success) {
        setAttachGalleryMsg('Documento salvo nos docs do paciente com sucesso!');
      } else {
        setAttachGalleryMsg('Erro ao salvar documento');
      }
    } catch (err: any) {
      setAttachGalleryMsg(err?.response?.data?.error || 'Erro ao salvar documento nos docs do paciente');
    } finally {
      setAttachingToGallery(false);
    }
  };

  const handleOpenLegacyDetails = (contact: Contact) => {
    setSelectedContact(contact);
    setOpenLeadDetailsDialog(true);
  };

  const handleCloseLeadDetails = () => {
    setOpenLeadDetailsDialog(false);
    setSelectedContact(null);
  };

  // Função para verificar se o lead está no estágio 4 (Venda Realizada)
  const isLeadInStage4 = async (contactId: string): Promise<boolean> => {
    try {
      console.log('🔍 Checking stage for contact:', contactId);
      // Usar a API /crm/all-leads que funciona corretamente
      const response = await axios.get(`${getApiUrl()}/crm/all-leads`);
      if (response.data.success && response.data.data) {
        const allLeads = response.data.data;
        console.log('📊 Total leads found:', allLeads.length);
        
        // Procurar o lead específico
        const targetLead = allLeads.find((lead: any) => lead.contacts?.id === contactId);
        
        if (targetLead) {
          console.log('🎯 Lead found in all-leads:', targetLead.contacts?.name);
          console.log('🎯 Stage order:', targetLead.crm_stages?.stage_order);
          const isStage4 = targetLead.crm_stages?.stage_order === 4;
          console.log('⭐ Is stage 4:', isStage4);
          return isStage4;
        } else {
          console.log('❌ Lead not found in all-leads');
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking lead stage:', error);
      return false;
    }
  };

  // Função para obter o estágio de qualquer lead
  const getLeadStage = (contactId: string): number | null => {
    return allLeadsStages.get(contactId) || null;
  };

  // Função para verificar estágios apenas dos contatos das conversas carregadas (otimizado)
  const checkAllLeadsStages = async (conversations: Conversation[]) => {
    console.log('🔍 checkAllLeadsStages called with conversations:', conversations.length);
    const stage4Set = new Set<string>();
    const stagesMap = new Map<string, number>();
    
    try {
      // Extrair apenas os contact_ids das conversas
      const contactIds = conversations
        .map(conv => conv.contact_id)
        .filter(Boolean) as string[];
      
      if (contactIds.length === 0) {
        console.log('⚠️ No contact IDs to check');
        setStage4Leads(stage4Set);
        setAllLeadsStages(stagesMap);
        return;
      }

      console.log(`📊 Buscando estágios para ${contactIds.length} contatos...`);
      
      // Buscar estágios apenas dos contatos das conversas carregadas (muito mais rápido)
      const response = await axios.post(`${getApiUrl()}/crm/contacts/stages`, {
        contact_ids: contactIds
      });
      
      if (response.data.success && response.data.data) {
        const stagesData = response.data.data; // { contact_id: stage_order }
        
        // Processar os estágios retornados
        Object.entries(stagesData).forEach(([contactId, stageOrder]) => {
          const stage = Number(stageOrder);
          if (stage) {
            stagesMap.set(contactId, stage);
            
            // Se for estágio 4, adicionar ao set
            if (stage === 4) {
              console.log('⭐ Adding to stage 4 set:', contactId);
              stage4Set.add(contactId);
            }
          }
        });
        
        console.log(`✅ Estágios encontrados para ${stagesMap.size} contatos`);
      }
    } catch (error) {
      console.error('Error fetching contact stages:', error);
    }
    
    console.log('🎯 Final stage 4 leads:', Array.from(stage4Set));
    console.log('🎯 All leads stages map:', Object.fromEntries(stagesMap));
    
    setStage4Leads(stage4Set);
    setAllLeadsStages(stagesMap);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showChatList, setShowChatList] = useState(true);

  const handleInboxCrmStageChanged = useCallback(() => {
    checkAllLeadsStages(conversations);
  }, [conversations]);

  // Loading da configuração
  if (configLoading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100%"
        minHeight={320}
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" color="text.secondary">Carregando configuração do inbox...</Typography>
      </Box>
    );
  }

  // Erro na configuração
  if (configError || !inboxConfig) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100%"
        minHeight={320}
        flexDirection="column"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          {configError || 'Inbox não encontrado'}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/inbox/whatsapp')}
        >
          Voltar ao Inbox Principal
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height="100%"
        minHeight={320}
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" color="text.secondary">
          Carregando conversas de {inboxConfig.name}...
        </Typography>
      </Box>
    );
  }

  // Função para voltar à lista no mobile
  const handleBackToList = () => {
    setShowChatList(true);
    if (isMobile) {
      setSelectedConversation('');
    }
  };

  return (
    <Box sx={{
      flex: 1,
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: { xs: '#f0f2f5', md: 'background.default' },
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Mobile: Chat em tela cheia quando selecionado */}
      {isMobile && selectedConversation && !showChatList ? (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header do chat mobile - extends behind status bar */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            pt: 'calc(env(safe-area-inset-top, 0px) + 10px)',
            pb: 1,
            bgcolor: '#25D366',
            color: 'white',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 1,
            flexShrink: 0,
          }}>
            <IconButton 
              onClick={handleBackToList}
              sx={{ 
                mr: 2, 
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              src={conversations.find(c => c.id === selectedConversation)?.contact?.avatar_url}
              sx={{ mr: 1.5, ...contactAvatarSx(conversations.find(c => c.id === selectedConversation)?.contact?.name, 32) }}
            >
              {conversations.find(c => c.id === selectedConversation)?.contact?.name
                ? getInboxInitials(conversations.find(c => c.id === selectedConversation)?.contact?.name)
                : <WhatsAppIcon sx={{ fontSize: 16 }} />}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: 'white',
                  fontSize: '0.9rem',
                  lineHeight: 1.3,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, maxWidth: '100%' }}>
                  {conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Conversa'}
                </Box>
                {(() => {
                  const ph = formatConversationPhoneDisplay(conversations.find(c => c.id === selectedConversation));
                  return ph ? (
                    <Box component="span" sx={{ flexShrink: 0, fontWeight: 500, opacity: 0.95, fontSize: '0.8rem' }}>
                      · {ph}
                    </Box>
                  ) : null;
                })()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WhatsAppIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                  WhatsApp
                </Typography>
              </Box>
              <LeadContactTags
                compact
                contactId={resolveConversationContactId(
                  conversations.find((c) => c.id === selectedConversation)
                )}
                onAssignmentsChanged={() => fetchConversations(true)}
              />
            </Box>
            
            {/* Botões de ação mobile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Atribuição de atendente */}
              {selectedConversation && (
                <Box sx={{ 
                  '& .MuiIconButton-root': {
                    color: 'white',
                  },
                  '& .MuiChip-root': {
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                  }
                }}>
                  <AttendantAssignment conversationId={selectedConversation} />
                </Box>
              )}
              {selectedConversation && (
                <InboxCrmStageMenu
                  contactId={conversations.find((c) => c.id === selectedConversation)?.contact?.id}
                  onStageChanged={handleInboxCrmStageChanged}
                  sx={{ color: 'white' }}
                />
              )}
              <IconButton
                size="small"
                sx={{ color: 'white' }}
                onClick={() => {
                  // Implementar toggle do bot
                  console.log('Toggle bot');
                }}
              >
                <SmartToyIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: 'white' }}
                onClick={() => setMobilePatientDrawer(true)}
                title="Paciente vinculado"
              >
                <PersonOutlineIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: '#14B8A6' }}
                onClick={() => {
                  const currentContact = conversations.find(c => c.id === selectedConversation)?.contact;
                  if (currentContact) {
                    handleContactClick(currentContact);
                  }
                }}
                title="Painel de Saúde"
              >
                <MedicalServicesIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: 'white' }}
                onClick={() => {
                  // Implementar exclusão
                  console.log('Excluir conversa');
                }}
              >
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          {selectedConversation && (
            <Box
              sx={{
                flexShrink: 0,
                px: 1.5,
                py: 0.5,
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ChatSupportEventsBanner conversationId={selectedConversation} compact />
            </Box>
          )}

          {/* Área de mensagens mobile */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              flex: 1, 
              p: 2, 
              overflow: 'auto',
              bgcolor: '#E8F5E8'
            }}>
              {/* Mensagens do chat */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '80%',
                        bgcolor: message.direction === 'outbound' 
                          ? '#DCF8C6'
                          : '#FFFFFF',
                        color: '#1C1E21',
                        p: 2,
                        borderRadius: 3,
                        boxShadow: 1
                      }}
                    >
                      <Typography variant="body2" sx={{ 
                        mb: 0.5,
                        lineHeight: 1.4
                      }}>
                        {message.content}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#65676B',
                          fontSize: '0.75rem',
                          display: 'block'
                        }}
                      >
                        {formatTime(message.sent_at)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                {/* Elemento para scroll automático */}
                <div ref={messagesEndRef} />
              </Box>
            </Box>

            {/* Input de mensagem mobile */}
            <Box sx={{
              px: 2,
              pt: 1,
              pb: 'max(8px, env(safe-area-inset-bottom, 8px))',
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,audio/*,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.ppt,.pptx,text/plain,.txt,application/zip,.zip"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              {selectedFile && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {(selectedFile.type.startsWith('audio/') || selectedFile.name.includes('recording')) && '🎤 '}
                    {selectedFile.name}
                  </Typography>
                  <IconButton size="small" onClick={handleCancelFile}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              {isRecording && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'error.light', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main', animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
                  <Typography variant="body2" sx={{ flex: 1, color: 'error.main', fontWeight: 500 }}>
                    Gravando... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </Typography>
                  <IconButton size="small" onClick={handleStopRecording} sx={{ color: 'error.main' }}>
                    <StopIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <IconButton
                  color="primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sendingMessage || isRecording}
                  sx={{ flexShrink: 0 }}
                >
                  <AttachFileIcon />
                </IconButton>
                <IconButton
                  color={isRecording ? 'error' : 'primary'}
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  disabled={sendingMessage || !!selectedFile}
                  sx={{ flexShrink: 0 }}
                >
                  {isRecording ? <StopIcon /> : <MicIcon />}
                </IconButton>
                <TextField
                  fullWidth
                  placeholder={selectedFile ? "Legenda (opcional)..." : "Digite sua mensagem..."}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isRecording) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={sendingMessage || isRecording}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                      bgcolor: '#f0f2f5'
                    }
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && !selectedFile) || sendingMessage || isRecording}
                >
                  {sendingMessage ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        /* Desktop: Layout normal */
        <Box sx={{
          display: 'flex',
          gap: { xs: 0, md: 0 },
          height: '100%',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}>
          {/* Lista de Conversas */}
          <Paper sx={{
            flex: { xs: showChatList ? '1' : '0', md: '0 0 380px' },
            minWidth: { xs: 'auto', md: 380 },
            height: '100%',
            minHeight: 0,
            display: { xs: showChatList ? 'flex' : 'none', md: 'flex' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 2 : 1, md: 1 },
            position: { xs: 'relative', md: 'static' },
            overflow: 'hidden',
            borderRadius: 0,
            borderRight: { xs: 0, md: 1 },
            borderColor: 'divider',
            flexDirection: 'column',
            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
            boxShadow: mode === 'dark' ? 'none' : '0 2px 8px rgba(0,0,0,0.04)',
          }}>
          <Box sx={{ 
            px: { xs: 1.5, md: 2.5 },
            pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 12px)', md: 2.5 },
            pb: { xs: 1.5, md: 2.5 },
            borderBottom: 1, 
            borderColor: 'divider', 
            bgcolor: mode === 'dark' ? '#252525' : '#f8f9fa',
            boxShadow: mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 2px rgba(0,0,0,0.05)',
            flexShrink: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isMobile && outletContext?.toggleDrawer && (
                <IconButton size="small" onClick={outletContext.toggleDrawer}
                  sx={{ color: 'text.primary', mr: 0.5 }}>
                  <MenuIcon sx={{ fontSize: 22 }} />
                </IconButton>
              )}
              <Box sx={{
                p: 0.75,
                borderRadius: '12px',
                bgcolor: mode === 'dark' ? 'rgba(37, 211, 102, 0.15)' : 'rgba(37, 211, 102, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <WhatsAppIcon sx={{ fontSize: 22, color: '#25D366' }} />
              </Box>
              <Typography variant="h6" sx={{ 
                color: 'text.primary', 
                fontWeight: 600,
                fontSize: '1.1rem',
                letterSpacing: '-0.01em',
                flex: 1,
              }}>
                {inboxConfig.name}
              </Typography>
              {inboxConfig.type === 'recovery' && (
                <Chip label="Recovery" size="small" color="warning" />
              )}
            </Box>
          </Box>
        
        {error && (
          <Box px={2.5} pt={2}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          </Box>
        )}
        
        {/* Campo de busca */}
        <Box px={2.5} pt={2} pb={1.5}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar por nome, telefone ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                '&:hover': {
                  bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                },
                '&.Mui-focused': {
                  bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                },
              },
            }}
          />
        </Box>

        <Box px={2} pb={1.5}>
          <InboxConversationTagFilter
            selectedTagIds={inboxFilterTagIds}
            onChange={setInboxFilterTagIds}
          />
        </Box>
        
        <List sx={{ 
          py: 1, 
          flex: 1, 
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
            '&:hover': {
              background: mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            },
          },
        }}>
           {(() => {
             // Filtrar conversas baseado no termo de busca
             const filteredConversations = searchTerm.trim() === '' 
               ? conversations 
               : conversations.filter((conv) => {
                   const searchLower = searchTerm.toLowerCase();
                   const name = conv.contact?.name?.toLowerCase() || '';
                   const phone = conv.contact?.phone?.toLowerCase() || '';
                   const email = conv.contact?.email?.toLowerCase() || '';
                   return name.includes(searchLower) || phone.includes(searchLower) || email.includes(searchLower);
                 });
             
             if (filteredConversations.length === 0) {
               return (
                 <Box sx={{ p: 4, textAlign: 'center' }}>
                   <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.7 }}>
                     {searchTerm.trim() ? 'Nenhuma conversa encontrada para sua busca' : 'Nenhuma conversa encontrada'}
                   </Typography>
                 </Box>
               );
             }
             
             return filteredConversations.map((conversation) => (
              <InboxConversationListItem
                key={conversation.id}
                conversation={conversation}
                selected={selectedConversation === conversation.id}
                mode={mode === 'dark' ? 'dark' : 'light'}
                isStage4Lead={
                  !!conversation.contact?.id && stage4Leads.has(conversation.contact.id)
                }
                formatTime={formatTime}
                onSelect={() => {
                  setSelectedConversation(conversation.id);
                  if (isMobile) {
                    setShowChatList(false);
                  }
                }}
              />
             ));
           })()}
          {/* Botão Carregar Mais */}
          {hasMoreConversations && (
            <Box sx={{ px: 2, py: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={loadMoreConversations}
                disabled={loadingMoreConversations}
                startIcon={loadingMoreConversations ? <CircularProgress size={18} /> : null}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  py: 1.25,
                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  color: 'text.secondary',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)',
                  },
                }}
              >
                {loadingMoreConversations ? 'Carregando...' : 'Carregar mais conversas'}
              </Button>
            </Box>
          )}
        </List>
      </Paper>

          {/* Área de Mensagens */}
          <Box sx={{
            flex: { xs: showChatList ? '0' : '1', md: 1 },
            minWidth: 0,
            minHeight: 0,
            display: { xs: showChatList ? 'none' : 'flex', md: 'flex' },
            width: { xs: '100%', md: 'auto' },
            zIndex: { xs: showChatList ? 1 : 2, md: 1 },
            position: { xs: 'relative', md: 'static' },
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
        {selectedConversation ? (
          <>
            {/* Cabeçalho da Conversa */}
            <Paper sx={{ 
              p: 2, 
              borderRadius: 0, 
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar
                  src={conversations.find(c => c.id === selectedConversation)?.contact?.avatar_url}
                  sx={contactAvatarSx(conversations.find(c => c.id === selectedConversation)?.contact?.name, 45)}
                >
                  {(() => {
                    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                    return contact?.name ? getInboxInitials(contact.name) : <PersonIcon sx={{ fontSize: 22 }} />;
                  })()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" component="span" sx={{ lineHeight: 1.2 }}>
                      {conversations.find(c => c.id === selectedConversation)?.contact?.name || 'Conversa'}
                    </Typography>
                    {(() => {
                      const ph = formatConversationPhoneDisplay(conversations.find(c => c.id === selectedConversation));
                      return ph ? (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          · {ph}
                        </Typography>
                      ) : null;
                    })()}
                    <WhatsAppIcon sx={{ fontSize: 20, color: '#25D366', flexShrink: 0 }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {inboxConfig.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* Atribuição de atendente */}
                  <AttendantAssignment conversationId={selectedConversation} />
                  <InboxCrmStageMenu
                    contactId={conversations.find((c) => c.id === selectedConversation)?.contact?.id}
                    onStageChanged={handleInboxCrmStageChanged}
                  />
                  {/* Botão de controle do bot */}
                  <BotToggleButton 
                    conversationId={selectedConversation}
                    size="small"
                    onStatusChange={(enabled) => {
                      console.log(`Bot ${enabled ? 'enabled' : 'disabled'} for conversation ${selectedConversation}`);
                    }}
                  />
                  
                  {/* Ícone de estrela para leads do estágio 4 */}
                  {(() => {
                    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                    console.log('🌟 Rendering star check - contact:', contact?.id, 'stage4Leads:', Array.from(stage4Leads), 'has contact:', stage4Leads.has(contact?.id || ''));
                    return contact && stage4Leads.has(contact.id) ? (
                      <Tooltip title="Lead no estágio 4 - Venda Realizada">
                        <IconButton
                          size="small"
                          sx={{ color: '#FFD700' }}
                        >
                          <StarIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null;
                  })()}
                  
                  <Tooltip title="Resumo do paciente">
                    <IconButton
                      size="small"
                      onClick={() => setMobilePatientDrawer(true)}
                      sx={{ color: '#0f766e', display: { xs: 'inline-flex', md: 'none' } }}
                    >
                      <PersonOutlineIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Painel de Saúde">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) {
                          handleContactClick(contact);
                        }
                      }}
                      sx={{ color: '#14B8A6' }}
                    >
                      <MedicalServicesIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Atualizar avatar">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) {
                          updateContactAvatar(contact.id);
                        }
                      }}
                      disabled={updatingAvatar === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                    >
                      {updatingAvatar === conversations.find(c => c.id === selectedConversation)?.contact?.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <RefreshIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir lead">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                        if (contact) {
                          deleteContact(contact.id);
                        }
                      }}
                      disabled={deletingContact === conversations.find(c => c.id === selectedConversation)?.contact?.id}
                    >
                      {deletingContact === conversations.find(c => c.id === selectedConversation)?.contact?.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <LeadContactTags
                contactId={resolveConversationContactId(
                  conversations.find((c) => c.id === selectedConversation)
                )}
                onAssignmentsChanged={() => fetchConversations(true)}
              />
            </Paper>

            {selectedConversation && (
              <Box
                sx={{
                  flexShrink: 0,
                  px: 2,
                  py: 0.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <ChatSupportEventsBanner conversationId={selectedConversation} compact />
              </Box>
            )}

            {/* Área de Mensagens */}
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              // Fundo adaptativo ao tema
              bgcolor: mode === 'dark' ? 'background.default' : '#E8F5E8',
              backgroundImage: mode === 'dark' ? 'none' : `
                radial-gradient(circle at 20% 20%, rgba(33, 150, 243, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(33, 150, 243, 0.10) 0%, transparent 50%),
                linear-gradient(135deg, 
                  rgba(33, 150, 243, 0.05) 0%, 
                  transparent 30%,
                  rgba(33, 150, 243, 0.08) 70%,
                  transparent 100%
                ),
                repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 60px,
                  rgba(33, 150, 243, 0.03) 60px,
                  rgba(33, 150, 243, 0.03) 62px
                )
              `,
              position: 'relative',
              // Adicionar um padrão de pontos para maior visibilidade
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `
                  radial-gradient(circle at 20px 20px, rgba(33, 150, 243, 0.1) 2px, transparent 2px)
                `,
                backgroundSize: '40px 40px',
                pointerEvents: 'none',
                opacity: 0.5
              }
            }}>
              {/* Container com scroll interno */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                position: 'relative',
                zIndex: 1,
                minHeight: 0, // Importante para flex funcionar
                px: 2, // Padding horizontal
                pb: 2 // Padding bottom
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}>
                {messages.map((message, index) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.direction === 'outbound' ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-end',
                    mb: 1,
                    gap: 1
                  }}
                >
                  {/* Avatar para mensagens recebidas */}
                  {message.direction === 'inbound' && (() => {
                    const contact = conversations.find(c => c.id === selectedConversation)?.contact;
                    const colors = getInboxAvatarColors(contact?.name || '?');
                    return (
                      <Avatar
                        src={contact?.avatar_url}
                        sx={contactAvatarSx(contact?.name, 32)}
                      >
                        {contact?.name
                          ? getInboxInitials(contact.name)
                          : <PersonIcon sx={{ fontSize: 16, color: colors.color }} />}
                      </Avatar>
                    );
                  })()}
                  
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      bgcolor: message.direction === 'outbound' 
                        ? '#DCF8C6' 
                        : (mode === 'dark' ? 'background.paper' : '#FFFFFF'),
                      color: message.direction === 'outbound' 
                        ? '#1C1E21' // Texto escuro para mensagens enviadas (verde)
                        : (mode === 'dark' ? 'text.primary' : '#1C1E21'),
                      borderRadius: message.direction === 'outbound' 
                        ? '18px 18px 4px 18px' 
                        : '18px 18px 18px 4px',
                      boxShadow: message.direction === 'outbound' 
                        ? '0 2px 4px rgba(37, 211, 102, 0.2)'
                        : (mode === 'dark' ? '0 2px 4px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.15)'),
                      border: 2,
                      borderColor: message.direction === 'outbound' 
                        ? '#B8E6B8' 
                        : (mode === 'dark' ? 'divider' : '#E0E0E0'),
                    }}
                  >
                    {/* Renderizar anexos */}
                    {message.attachments && message.attachments.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {message.attachments.map((attachment, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            {attachment.type === 'image' && (
                              <Box sx={{ maxWidth: '100%', width: '100%' }}>
                                {/* Imagem */}
                                <Box sx={{ 
                                  borderRadius: '8px 8px 0 0',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  width: '100%',
                                  position: 'relative',
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  borderBottom: 'none',
                                  display: 'block',
                                  cursor: 'pointer',
                                }}
                                  onClick={() => setExpandedImage(normalizeAttachmentUrl(attachment.url))}
                                >
                                  <img 
                                    src={normalizeAttachmentUrl(attachment.url)} 
                                    alt="Imagem"
                                    style={{ 
                                      width: '100%', 
                                      height: 'auto',
                                      maxHeight: 400,
                                      objectFit: 'contain',
                                      display: 'block',
                                    }}
                                    onError={(e) => { e.currentTarget.style.opacity = '0.4'; }}
                                  />
                                  {/* Hint ampliar */}
                                  <Box sx={{ position: 'absolute', top: 6, left: 6, zIndex: 5, display: 'flex', alignItems: 'center', gap: 0.5,
                                    bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', borderRadius: 1, px: 0.8, py: 0.3, fontSize: '0.7rem', pointerEvents: 'none' }}>
                                    <ZoomInIcon sx={{ fontSize: 14 }} />
                                    Ampliar
                                  </Box>
                                </Box>
                                {/* Barra de ações — sempre visível */}
                                <Box sx={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5,
                                  bgcolor: 'rgba(0,0,0,0.55)', borderRadius: '0 0 8px 8px',
                                  border: '1px solid rgba(255,255,255,0.1)', borderTop: 'none',
                                  px: 1, py: 0.5,
                                }}>
                                  {(attachment.width || attachment.height) && (
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', flex: 1 }}>
                                      {attachment.width && attachment.height && `${attachment.width}×${attachment.height}`}
                                    </Typography>
                                  )}
                                  <Box sx={{ flex: 1 }} />
                                  <Tooltip title="Baixar imagem">
                                    <IconButton size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const link = document.createElement('a');
                                        link.href = normalizeAttachmentUrl(attachment.url);
                                        link.download = `image_${message.id}.jpg`;
                                        link.target = '_blank';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
                                    >
                                      <DownloadIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Salvar no banco de imagens do paciente">
                                    <IconButton size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAttachImageToGallery(normalizeAttachmentUrl(attachment.url));
                                      }}
                                      sx={{ color: '#5eead4', p: 0.5, '&:hover': { color: '#14B8A6', bgcolor: 'rgba(20,184,166,0.15)' } }}
                                    >
                                      <MedicalServicesIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            )}
                            {attachment.type === 'video' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                maxWidth: '100%',
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                              }}>
                                <Box sx={{ position: 'relative' }}>
                                  <video 
                                    controls
                                    preload="metadata"
                                    style={{ 
                                      width: '100%', 
                                      height: 'auto',
                                      maxHeight: 400,
                                      display: 'block'
                                    }}
                                  >
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/mp4" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/webm" />
                                    <source src={normalizeAttachmentUrl(attachment.url)} type="video/ogg" />
                                    Seu navegador não suporta vídeos.
                                  </video>
                                  <Box sx={{ 
                                    position: 'absolute', 
                                    top: 8, 
                                    right: 8,
                                    zIndex: 10
                                  }}>
                                    <IconButton 
                                      size="small"
                                      onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = normalizeAttachmentUrl(attachment.url);
                                        link.download = `video_${message.id}.mp4`;
                                        link.target = '_blank';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      sx={{ 
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.8)' }
                                      }}
                                    >
                                      <DownloadIcon />
                                    </IconButton>
                                  </Box>
                                </Box>
                                {attachment.duration && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, px: 1, color: 'text.secondary' }}>
                                    Duração: {Math.floor(attachment.duration / 60)}:{(attachment.duration % 60).toString().padStart(2, '0')}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {attachment.type === 'audio' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)'
                              }}>
                                <audio controls style={{ width: '100%' }}>
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mp3" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/wav" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                  Seu navegador não suporta áudio.
                                </audio>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  {attachment.title && (
                                    <Typography variant="caption" sx={{ flex: 1 }}>
                                      {attachment.title}
                                      {attachment.performer && ` - ${attachment.performer}`}
                                    </Typography>
                                  )}
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = normalizeAttachmentUrl(attachment.url);
                                      link.download = attachment.title 
                                        ? `${attachment.title}.mp3` 
                                        : `audio_${message.id}.mp3`;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                            {attachment.type === 'voice' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)'
                              }}>
                                <audio controls style={{ width: '100%' }}>
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/ogg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/mpeg" />
                                  <source src={normalizeAttachmentUrl(attachment.url)} type="audio/webm" />
                                  Seu navegador não suporta áudio.
                                </audio>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ flex: 1 }}>
                                    🎤 Mensagem de voz
                                  </Typography>
                                  <IconButton 
                                    size="small"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = normalizeAttachmentUrl(attachment.url);
                                      link.download = `voice_${message.id}.ogg`;
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            )}
                            {attachment.type === 'document' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <Typography variant="body2">📎</Typography>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {attachment.file_name || 'Documento'}
                                </Typography>
                                <Tooltip title="Salvar nos documentos do paciente">
                                  <IconButton size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAttachDocToHealthcare(
                                        normalizeAttachmentUrl(attachment.url),
                                        attachment.file_name || 'documento_chat',
                                        attachment.mime_type
                                      );
                                    }}
                                    sx={{ color: '#14B8A6', '&:hover': { color: '#0d9488' } }}
                                  >
                                    <MedicalServicesIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <IconButton 
                                  size="small" 
                                  onClick={() => window.open(normalizeAttachmentUrl(attachment.url), '_blank')}
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </Box>
                            )}
                            {attachment.type === 'sticker' && (
                              <Box sx={{ 
                                borderRadius: 1, 
                                overflow: 'hidden',
                                display: 'inline-block'
                              }}>
                                <img 
                                  src={normalizeAttachmentUrl(attachment.url)} 
                                  alt="Sticker"
                                  style={{ 
                                    width: 120, 
                                    height: 120,
                                    objectFit: 'contain'
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {/* Renderizar texto da mensagem (caption) */}
                    {/* Mostrar apenas se não for mensagem genérica ou apenas emoji quando houver attachments */}
                    {message.content && (
                      (() => {
                        const hasImageAttachment = message.attachments?.some((att: any) => att.type === 'image');
                        const hasVideoAttachment = message.attachments?.some((att: any) => att.type === 'video');
                        const hasAudioAttachment = message.attachments?.some((att: any) => att.type === 'audio');
                        const isOnlyEmoji = /^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]$/u.test(message.content.trim());
                        const isGenericMessage = message.content.includes('Mensagem image') || message.content.includes('Mensagem video') || message.content.includes('Mensagem audio');
                        
                        // Se houver attachment de imagem/vídeo/áudio e o conteúdo for apenas emoji ou mensagem genérica, não mostrar
                        // Mas se houver texto real (caption), mostrar
                        if ((hasImageAttachment || hasVideoAttachment || hasAudioAttachment) && (isOnlyEmoji || isGenericMessage)) {
                          return null;
                        }
                        // Mostrar caption se houver
                        return <Typography sx={{ mt: hasImageAttachment || hasVideoAttachment ? 1 : 0 }}>{message.content}</Typography>;
                      })()
                    )}
                    
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {formatTime(message.sent_at)}
                    </Typography>
                                      </Paper>
                  </Box>
                ))}
                {/* Elemento para scroll automático */}
                <div ref={messagesEndRef} />
                </Box>
              </Box>
            </Box>

            {/* Campo de Envio */}
            <Paper sx={{ 
              p: 2, 
              borderRadius: 0, 
              borderTop: 1, 
              borderColor: 'divider', 
              mt: 'auto',
              bgcolor: 'background.paper',
              boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              {/* Indicador de arquivo selecionado */}
              {selectedFile && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {(selectedFile.type.startsWith('audio/') || selectedFile.name.includes('recording')) && '🎤 '}
                    {selectedFile.name}
                    {selectedFile.size > 0 && ` (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)`}
                  </Typography>
                  <IconButton size="small" onClick={handleCancelFile}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* Indicador de gravação */}
              {isRecording && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'error.light', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: 'error.main',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 }
                    }
                  }} />
                  <Typography variant="body2" sx={{ flex: 1, color: 'error.main', fontWeight: 500 }}>
                    Gravando... {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                  </Typography>
                  <IconButton size="small" onClick={handleStopRecording} sx={{ color: 'error.main' }}>
                    <StopIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Box display="flex" gap={1} alignItems="flex-end">
                {/* Input de arquivo oculto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,audio/*,.pdf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.ppt,.pptx,text/plain,.txt,application/zip,.zip"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />

                {/* Botão de anexar arquivo */}
                <IconButton
                  color="primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sendingMessage || isRecording}
                  sx={{ flexShrink: 0 }}
                >
                  <AttachFileIcon />
                </IconButton>

                {/* Botão de gravar áudio */}
                <IconButton
                  color={isRecording ? 'error' : 'primary'}
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  disabled={sendingMessage || !!selectedFile}
                  sx={{ flexShrink: 0 }}
                >
                  {isRecording ? <StopIcon /> : <MicIcon />}
                </IconButton>

                {/* Botão de mensagens rápidas */}
                <QuickMessagesButton
                  onSelectMessage={(message) => {
                    setNewMessage(message);
                    setTimeout(() => {
                      messageInputRef.current?.focus();
                    }, 0);
                  }}
                  channel="whatsapp"
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={selectedFile ? "Adicione uma legenda (opcional)..." : "Digite sua mensagem..."}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isRecording) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  onPaste={handlePasteImage}
                  inputRef={messageInputRef}
                  disabled={sendingMessage || isRecording}
                  multiline
                  maxRows={4}
                />
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && !selectedFile) || sendingMessage || isRecording}
                  sx={{ flexShrink: 0 }}
                >
                  {sendingMessage ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
              </Box>
            </Paper>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Selecione uma conversa para começar
            </Typography>
          </Box>
        )}
          </Box>

          {selectedConversation && !isMobile && (
            <InboxPatientSidebar
              contactId={selectedConversationContact?.id ?? null}
              contactName={selectedConversationContact?.name}
              contactPhone={selectedConversationContact?.phone}
              reloadToken={patientSidebarReload}
              onSendMessageToChat={sendMessageToChat}
              onLinkPatient={() => {
                if (selectedConversationContact) {
                  openHealthcareForContact(selectedConversationContact);
                }
              }}
              onOpenHealthcarePanel={(opts) => {
                if (selectedConversationContact) {
                  openHealthcareForContact(selectedConversationContact, opts);
                  setMobilePatientDrawer(false);
                }
              }}
            />
          )}
        </Box>
      )}

      {/* Drawer paciente (mobile / tablet) */}
      <Drawer
        anchor="right"
        open={mobilePatientDrawer && !!selectedConversation}
        onClose={() => setMobilePatientDrawer(false)}
        PaperProps={{
          sx: { width: { xs: 'min(100vw, 360px)', sm: 360 }, maxWidth: '100vw' },
        }}
      >
        {selectedConversation && (
          <InboxPatientSidebar
            compact
            contactId={selectedConversationContact?.id ?? null}
            contactName={selectedConversationContact?.name}
            contactPhone={selectedConversationContact?.phone}
            reloadToken={patientSidebarReload}
            onSendMessageToChat={sendMessageToChat}
            onLinkPatient={() => {
              if (selectedConversationContact) {
                openHealthcareForContact(selectedConversationContact);
                setMobilePatientDrawer(false);
              }
            }}
            onOpenHealthcarePanel={(opts) => {
              if (selectedConversationContact) {
                openHealthcareForContact(selectedConversationContact, opts);
                setMobilePatientDrawer(false);
              }
            }}
          />
        )}
      </Drawer>

      {/* Dialog para Imagem Ampliada */}
      <Dialog 
        open={!!expandedImage} 
        onClose={() => setExpandedImage(null)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: '90vw',
            maxHeight: '90vh',
            m: 2,
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          color: '#fff',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Imagem Ampliada
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => {
                if (expandedImage) {
                  const link = document.createElement('a');
                  link.href = normalizeAttachmentUrl(expandedImage);
                  link.download = `image_${Date.now()}.jpg`;
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
              sx={{ 
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={() => setExpandedImage(null)}
              sx={{ 
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          position: 'relative',
          minHeight: '60vh'
        }}>
          {expandedImage && (
            <img 
              src={normalizeAttachmentUrl(expandedImage || '')} 
              alt="Imagem ampliada"
              style={{ 
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                cursor: 'zoom-out'
              }}
              onClick={() => setExpandedImage(null)}
              onError={(e) => {
                console.error('Erro ao carregar imagem:', e);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para Detalhes do Lead */}
      <Dialog open={openLeadDetailsDialog} onClose={handleCloseLeadDetails} maxWidth="md" fullWidth>
        <DialogTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={selectedContact?.avatar_url || undefined}
                sx={{
                  bgcolor: selectedContact?.avatar_url ? 'transparent' : '#229ED9' + '15',
                  color: '#229ED9',
                  fontWeight: 600,
                  width: 48,
                  height: 48,
                  fontSize: '1.2rem'
                }}
              >
                {!selectedContact?.avatar_url && (selectedContact?.name || 'Lead').charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedContact?.name || 'Lead'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WhatsAppIcon sx={{ fontSize: 20, color: '#25D366' }} />
                  <Typography variant="body2" color="text.secondary">
                    {inboxConfig.name} Lead
                  </Typography>
                </Box>
              </Box>
            </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Informações Básicas */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                Informações Básicas
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Nome
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedContact?.name || 'Não informado'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedContact?.email || 'Não informado'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Telefone
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedContact?.phone || 'Não informado'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Canal
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsAppIcon sx={{ fontSize: 18, color: '#25D366' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      WhatsApp
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* IDs do WhatsApp */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                IDs do WhatsApp
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    WhatsApp ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.custom_fields?.whatsapp_id || 'Não disponível'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Username
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.custom_fields?.username ? `@${selectedContact.custom_fields.username}` : 'Não disponível'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    ID Interno
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                    {selectedContact?.id || 'Não disponível'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Campos Customizados */}
            {selectedContact?.custom_fields && Object.keys(selectedContact.custom_fields).length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  Campos Customizados
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {Object.entries(selectedContact.custom_fields).map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {String(value) || 'Não disponível'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLeadDetails}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Healthcare Panel */}
      <HealthcarePanel
        open={openHealthcarePanel}
        contactId={selectedContact?.id || null}
        contactName={selectedContact?.name}
        contactPhone={selectedContact?.phone}
        contactEmail={selectedContact?.email}
        initialTab={healthcarePanelTab}
        openScheduleOnMount={healthcareOpenSchedule}
        onPatientLinked={() => setPatientSidebarReload((t) => t + 1)}
        onSendMessage={(message) => {
          setNewMessage(message);
          setOpenHealthcarePanel(false);
        }}
        onSendMediaUrl={(url, type, fileName) => {
          handleSendMediaFromHealthcare(url, type, fileName);
          setOpenHealthcarePanel(false);
        }}
        onClose={handleCloseHealthcarePanel}
      />

      {/* Feedback: salvar imagem na galeria */}
      {(attachingToGallery || attachGalleryMsg) && (
        <Box sx={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, minWidth: 280, maxWidth: 400,
        }}>
          <Alert
            severity={attachingToGallery ? 'info' : ((attachGalleryMsg.includes('sucesso') || attachGalleryMsg.includes('salv')) ? 'success' : 'error')}
            onClose={attachingToGallery ? undefined : () => setAttachGalleryMsg('')}
            sx={{ boxShadow: 4, borderRadius: 2 }}
          >
            {attachingToGallery ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Salvando no prontuário do paciente...
              </Box>
            ) : attachGalleryMsg}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default DynamicWhatsAppInbox;
